import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import api from "../../api/api";
import { useAuth } from "../../features/auth/AuthContext";
import { ReactSVG } from "react-svg";
import { search, suggestion_icon } from "../../assets/images/images";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

function UniversalSearch() {
  const { token } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const navigate = useNavigate();
  const globalState = useSelector((state) => state.globalState);
  const isTeacher = globalState.role === "Teacher";
  const [key, setKey] = useState(0); // For forcing re-render
  const [isOpen, setIsOpen] = useState(false);

  const handleOptionSelect = (event, value) => {
    // Ignore the special "NO_RESULTS" option
    if (value === "NO_RESULTS") {
      return;
    }

    if (value && value.item_type) {
      let path;
      let navigationState = { redirected_from: "universal_search" };

      switch (value.item_type) {
        case "Course":
          path = isTeacher
            ? `/course/${value.item_id}/view`
            : `/course/${value.item_id}`;
          // Add course data to navigation state for teachers
          if (isTeacher) {
            navigationState = {
              ...navigationState,
              course: {
                course_id: value.item_id,
                name: value.name,
              },
              course_id: value.item_id,
            };
          }
          break;
        case "Lesson":
        case "Assignment":
          path = isTeacher
            ? `/course/${value.related_course_id}/view`
            : `/course/${value.related_course_id}`;
          // Add course data to navigation state for teachers
          if (isTeacher) {
            navigationState = {
              ...navigationState,
              course: {
                course_id: value.related_course_id,
                name: value.name,
              },
              course_id: value.related_course_id,
            };
          }
          break;
        case "Student":
          path = `/student/${value.item_id}`;
          break;
        case "Teacher":
          path = `/teacher/${value.item_id}`;
          break;
        default:
          path = isTeacher
            ? `/course/${value.item_id}/view`
            : `/course/${value.item_id}`;
          // Add course data to navigation state for teachers
          if (isTeacher) {
            navigationState = {
              ...navigationState,
              course: {
                course_id: value.item_id,
                name: value.name,
              },
              course_id: value.item_id,
            };
          }
      }

      if (path) {
        setInputValue(""); // Attempt to clear the input value
        setOptions([]); // Clear options to prevent state conflicts
        setIsOpen(false); // Close the dropdown
        navigate(path, { state: navigationState });
        setKey((prevKey) => prevKey + 1); // Increment the key to force re-render
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (options.length) {
        handleOptionSelect(e, options[0], "");
      }
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await api.get(
          `auth/universal-search/?search=${inputValue}`,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Token ${token}`,
            },
          },
        );

        // If no results, add a special "no results" option
        if (response.data.results && response.data.results.length === 0) {
          setOptions(["NO_RESULTS"]);
          console.log("No results, setting NO_RESULTS option");
        } else {
          setOptions(response.data.results || []);
          console.log("Options set to:", response.data.results || []);
        }
      } catch (error) {
        setOptions([]);
      }
    };

    // Debounce the fetch suggestions to avoid too many API calls
    if (inputValue) {
      const timerId = setTimeout(() => {
        fetchSuggestions();
      }, 300);
      return () => clearTimeout(timerId);
    } else {
      // Clear options when input is empty
      setOptions([]);
    }

    // Clear the timeout when the component is unmounted or the inputValue is changed
  }, [inputValue, token]);

  return (
    <Grid
      key={key}
      container
      justifyContent="center"
      sx={{ width: { xs: 150, sm: 300 } }}
    >
      <Autocomplete
        fullWidth
        freeSolo
        inputValue={inputValue}
        filterOptions={(x) => x}
        onInputChange={(_, newValue) => {
          setInputValue(newValue);
          // Always keep dropdown open when there's input
          if (newValue.length > 0) {
            setIsOpen(true);
          } else {
            setIsOpen(false);
          }
        }}
        onChange={handleOptionSelect}
        onKeyUp={handleKeyPress}
        options={options}
        getOptionLabel={(option) => {
          if (option === "NO_RESULTS") return "";
          return option.name || "";
        }}
        noOptionsText="No results found"
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        renderOption={(props, option) => {
          // Handle the special "no results" case
          if (option === "NO_RESULTS") {
            return (
              <Box component="li" {...props}>
                <Box sx={{ p: 2, textAlign: "center", width: "100%" }}>
                  <Typography variant="body2" color="text.secondary">
                    No results found
                  </Typography>
                </Box>
              </Box>
            );
          }

          // Normal option rendering
          return (
            <Box component="li" {...props} key={option.uid}>
              <ReactSVG src={suggestion_icon} />
              <Grid pl={2}>
                <Typography
                  fontSize={14}
                  fontWeight={300}
                  variant="subtitle1"
                  color="#252525"
                >
                  {option.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="#0068FF"
                  fontSize={12}
                  fontWeight={300}
                  style={{ marginLeft: "auto" }}
                >
                  ({option.item_type})
                </Typography>
              </Grid>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="universal-search-input-main"
            placeholder="Search Courses"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <ReactSVG src={search} style={{ margin: "0 10px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "rgba(242, 242, 242, 1)",
                "& fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "transparent",
                },
              },
              "& .MuiAutocomplete-inputRoot": {
                padding: 0,
              },
            }}
          />
        )}
      />
    </Grid>
  );
}

export default UniversalSearch;
